/**
 * Format a Date object to YYYY-MM-DD string in local timezone.
 * Used for API requests where we want to preserve the local date without timezone conversion.
 */
export function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format a Date object for display in the UI.
 * Uses the browser's locale settings for consistent date display.
 * Handles timezone conversion to ensure the correct local date is displayed.
 */
export function formatDisplayDate(date: Date | string): string {
  if (!date) return '—';

  let dateObj: Date;

  if (typeof date === 'string') {
    // If the string already includes a time component (after the "T"), parse directly.
    // Otherwise, append a local-midnight time to avoid timezone shifts.
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      dateObj = new Date(`${date}T00:00:00`);
    } else {
      // Normalize microseconds (more than 3 fractional digits) because JS Date only supports milliseconds
      let normalized = date.replace(/\.\d{3}\d*(?=[Z+-])/, (match) => match.slice(0, 4));
      // Remove the colon in timezone offset to improve Safari compatibility ("+00:00" -> "+0000")
      normalized = normalized.replace(/([+-]\d{2}):(\d{2})$/, '$1$2');
      dateObj = new Date(normalized);
    }
  } else {
    dateObj = date;
  }

  if (isNaN(dateObj.getTime())) {
    console.warn('formatDisplayDate received unparsable date:', date);
    return 'Invalid Date';
  }

  return dateObj.toLocaleDateString();
}

export const formatDate = (date: string, format: 'time' | 'full' = 'full'): string => {
  const d = new Date(date);

  if (format === 'time') {
    return d.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Parses a date string (potentially with time/timezone) into a Date object
 * representing the correct local date, ignoring time/timezone components.
 * Useful for initializing date pickers with backend dates.
 */
export function parseLocalDateString(dateString: string | null | undefined): Date | null {
  if (!dateString) {
    return null;
  }

  // Extract the "YYYY-MM-DD" part, handling potential missing 'T' separator
  const datePart = dateString.split('T')[0];
  if (!datePart || !/\d{4}-\d{2}-\d{2}/.test(datePart)) {
    console.warn('Invalid date string format for parseLocalDateString:', dateString);
    return null; // Or return new Date() or handle error differently
  }

  const [year, month, day] = datePart.split('-').map(Number);

  // Check for valid numbers after splitting
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    console.warn('Invalid date components after parsing:', dateString);
    return null;
  }

  // Create Date object using local time components.
  // NOTE: Month is 0-indexed in JS Date constructor!
  // Use Date.UTC to avoid timezone interpretation issues during construction, then let the object represent local time.
  // Creating a Date object this way ensures it represents midnight local time on the specified day.
  const localDate = new Date(year, month - 1, day);

  // Optional: Verify the date hasn't been shifted due to DST or timezone issues
  // This check can sometimes be necessary depending on JS environment behavior
  if (localDate.getFullYear() !== year || localDate.getMonth() !== month - 1 || localDate.getDate() !== day) {
    console.warn(`Date shift detected during parsing of ${dateString}. Check timezone/DST issues.`);
    // Handle potential shift if necessary, e.g., return null or attempt adjustment
  }

  return localDate;
} 